import { escapeXml, formatDateXml, buildXmlPayload, sendXmlRequest } from './core';

// ============ PO TRANSACTION CREATE CONFIGURATION ============
const PO_CREATE_HEADER_FIELDS = ['transactiontype', 'datecreated', 'createdfrom', 'vendorid', 'documentno', 'referenceno', 'vendordocno', 'datedue'];
const PO_DATE_FIELDS = ['datecreated', 'datedue'];
const PO_LINE_ITEM_FIELDS = ['itemid', 'itemdesc', 'quantity', 'unit', 'price', 'sourcelinekey', 'locationid', 'departmentid', 'memo', 'form1099', 'form1099type', 'form1099box', 'projectid', 'taskid', 'costtypeid', 'customerid', 'vendorid', 'classid', 'billable', 'retainagepercentage', 'trx_amountretained', 'conversiontype'];

const PO_LINE_FIELD_MAP = {
  'projectid': 'projectid2',
  'vendorid': 'vendorid2',
};

// ============ FUNCTIONS ============
export const getPOCreateSummary = (records) => {
  const grouped = {};
  const missingDocNo = [];
  
  records.forEach((rec, idx) => {
    const docNo = rec.documentno || rec.DOCUMENTNO;
    if (!docNo) { missingDocNo.push(idx + 2); return; }
    if (!grouped[docNo]) grouped[docNo] = 0;
    grouped[docNo]++;
  });
  
  return {
    documents: Object.entries(grouped).map(([docNo, count]) => ({ docNo, lineCount: count })),
    missingDocNoRows: missingDocNo,
    totalLines: records.length,
    totalDocuments: Object.keys(grouped).length
  };
};

export const buildPOCreatePreviewXml = (records, excludedFields = []) => {
  const grouped = {};
  records.forEach(rec => {
    const docNo = rec.documentno || rec.DOCUMENTNO;
    if (!docNo) return;
    if (!grouped[docNo]) grouped[docNo] = [];
    grouped[docNo].push(rec);
  });
  
  const docNos = Object.keys(grouped);
  if (docNos.length === 0) return '<!-- No valid records with documentno -->';
  
  const firstDocNo = docNos[0];
  const firstDocLines = grouped[firstDocNo];
  const firstLine = firstDocLines[0];
  
  let headerXml = '';
  PO_CREATE_HEADER_FIELDS.forEach(field => {
    if (excludedFields.includes(field)) return;
    const val = firstLine[field] || firstLine[field.toUpperCase()] || '';
    if (PO_DATE_FIELDS.includes(field)) {
      headerXml += `\n        ${formatDateXml(val, field)}`;
    } else if (val || field === 'documentno' || field === 'transactiontype') {
      headerXml += `\n        <${field}>${escapeXml(String(val))}</${field}>`;
    }
  });
  
  headerXml += `\n        <returnto>\n            <contactname/>\n        </returnto>`;
  headerXml += `\n        <payto>\n            <contactname/>\n        </payto>`;
  
  const headerProjectId = firstLine.projectid || firstLine.PROJECTID;
  if (headerProjectId && !excludedFields.includes('projectid_header')) {
    headerXml += `\n        <projectid>${escapeXml(String(headerProjectId))}</projectid>`;
  }
  
  const sampleLines = firstDocLines.slice(0, 2);
  const linesXml = sampleLines.map(line => {
    let lineFieldsXml = '';
    PO_LINE_ITEM_FIELDS.forEach(field => {
      if (excludedFields.includes(field)) return;
      const mappedField = PO_LINE_FIELD_MAP[field];
      const val = line[mappedField] || line[mappedField?.toUpperCase()] || line[field] || line[field.toUpperCase()];
      if (val !== undefined && val !== null && val !== '') {
        lineFieldsXml += `\n                <${field}>${escapeXml(String(val))}</${field}>`;
      }
    });
    return `<potransitem>${lineFieldsXml}\n            </potransitem>`;
  }).join('\n            ');
  
  const moreEntriesNote = firstDocLines.length > 2 ? `\n            <!-- ... and ${firstDocLines.length - 2} more line(s) ... -->` : '';
  const moreDocsNote = docNos.length > 1 ? `\n\n<!-- ... and ${docNos.length - 1} more document(s) will be created ... -->` : '';
  
  return `<function controlid="${escapeXml(firstDocNo)}">
    <create_potransaction>${headerXml}
        <potransitems>
            ${linesXml}${moreEntriesNote}
        </potransitems>
    </create_potransaction>
</function>${moreDocsNote}`;
};

export const createPOTransactions = async (config, records, excludedFields = []) => {
  const grouped = {};
  records.forEach(rec => {
    const docNo = rec.documentno || rec.DOCUMENTNO;
    if (!docNo) return;
    if (!grouped[docNo]) grouped[docNo] = [];
    grouped[docNo].push(rec);
  });
  
  const functions = Object.entries(grouped).map(([docNo, lines]) => {
    const firstLine = lines[0];
    
    let headerXml = '';
    PO_CREATE_HEADER_FIELDS.forEach(field => {
      if (excludedFields.includes(field)) return;
      const val = firstLine[field] || firstLine[field.toUpperCase()] || '';
      if (PO_DATE_FIELDS.includes(field)) {
        headerXml += formatDateXml(val, field);
      } else if (val || field === 'documentno' || field === 'transactiontype') {
        headerXml += `<${field}>${escapeXml(String(val))}</${field}>`;
      }
    });
    
    headerXml += '<returnto><contactname/></returnto>';
    headerXml += '<payto><contactname/></payto>';
    
    const headerProjectId = firstLine.projectid || firstLine.PROJECTID;
    if (headerProjectId && !excludedFields.includes('projectid_header')) {
      headerXml += `<projectid>${escapeXml(String(headerProjectId))}</projectid>`;
    }
    
    const linesXml = lines.map(line => {
      let lineFieldsXml = '';
      PO_LINE_ITEM_FIELDS.forEach(field => {
        if (excludedFields.includes(field)) return;
        const mappedField = PO_LINE_FIELD_MAP[field];
        const val = line[mappedField] || line[mappedField?.toUpperCase()] || line[field] || line[field.toUpperCase()];
        if (val !== undefined && val !== null && val !== '') {
          lineFieldsXml += `<${field}>${escapeXml(String(val))}</${field}>`;
        }
      });
      return `<potransitem>${lineFieldsXml}</potransitem>`;
    }).join('');
    
    return `<function controlid="${escapeXml(docNo)}"><create_potransaction>${headerXml}<potransitems>${linesXml}</potransitems></create_potransaction></function>`;
  }).join('');
  
  const xmlPayload = buildXmlPayload(config, functions);
  const result = await sendXmlRequest(config, 'PODOCUMENT', xmlPayload, 'PO CREATE');
  return result.results;
};

export const getPOExcludableFields = () => {
  const fieldLabels = {
    'projectid_header': 'projectid (header)',
    'projectid': 'projectid (line)',
    'vendorid': 'vendorid (line)',
  };
  
  return [
    { id: 'projectid_header', label: fieldLabels['projectid_header'], group: 'header' },
    { id: 'line_no', label: 'line_no (reference only)', group: 'line', alwaysExcluded: true },
    ...PO_LINE_ITEM_FIELDS.map(f => ({ 
      id: f, 
      label: fieldLabels[f] || f, 
      group: 'line' 
    }))
  ];
};
